'use client'

import { SearchResultHorizontal } from '@components/SearchResultHorizontal'
import { MeiliConsolidatedBookDto } from '@hermes/domains'
import { Box, Button, InfiniteList, List, Typography, useTheme } from '@hermes/web-components'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { ArrowUpwardRounded } from '@mui/icons-material'
import { Divider } from '@mui/material'
import React, { useMemo } from 'react'
import { useBookSearch, useBookSearchRefinements } from '@hermes/search'
import { BaseImage } from './BaseImage'
import { SearchResultHorizontalSkeleton } from './SearchResultHorizontalSkeleton'

const getBooksToInclude = (hit: MeiliConsolidatedBookDto, includedFormats: string[], includedLanguages: string[]) => {
  const formatFilterActive = includedFormats.length > 0
  const languageFilterActive = includedLanguages.length > 0

  let booksToInclude = hit.books

  if (formatFilterActive) {
    booksToInclude = booksToInclude.filter((book) => includedFormats.includes(book.format))
  }

  if (languageFilterActive) {
    booksToInclude = booksToInclude.filter((book) => includedLanguages.includes(book.languages[0]))
  }

  return booksToInclude
}

const SearchResultHit: React.FC<{
  hit: MeiliConsolidatedBookDto
  includedFormats: string[]
  includedLanguages: string[]
}> = React.memo(({ hit, includedFormats, includedLanguages }) => {
  const theme = useTheme()
  const { down } = theme.breakpoints

  const sortedBooks = useMemo(() => {
    const booksToSort = getBooksToInclude(hit, includedFormats, includedLanguages)

    return booksToSort.sort((book1, book2) => {
      if (book1.availability.shipping.available === book2.availability.shipping.available) {
        return 0
      }
      if (book1.availability.shipping.available) {
        return -1
      }
      if (book1.availability.meetup.available) {
        return -1
      }
      return 1
    })
  }, [hit.books, includedFormats, includedLanguages])

  const booksToInclude = sortedBooks

  if (booksToInclude.length === 0) return <></>

  const bookWithSortedVersions = {
    ...hit,
    books: booksToInclude
  }

  return (
    <Box>
      <SearchResultHorizontal book={bookWithSortedVersions} />
      <Divider
        sx={{
          marginTop: '20px',
          marginBottom: '20px',
          [down('sm')]: { marginTop: '8px', marginBottom: '8px' }
        }}
      />
    </Box>
  )
})

const SkeletonLoadingList: React.FC<{ numberOfSkeletonCards: number }> = ({ numberOfSkeletonCards }) => {
  const theme = useTheme()
  const { down } = theme.breakpoints
  return (
    <Box paddingTop={'8px'}>
      {Array.from({ length: numberOfSkeletonCards }).map((_, index) => (
        <Box key={index}>
          <SearchResultHorizontalSkeleton />
          <Divider
            sx={{
              marginTop: '20px',
              marginBottom: '20px',
              [down('sm')]: { marginTop: '8px', marginBottom: '8px' }
            }}
          />
        </Box>
      ))}
    </Box>
  )
}

const EmptyResultState: React.FC<{
  showBackToTopButton?: boolean
}> = ({ showBackToTopButton = true }) => {
  const { t } = useTranslateMessage()
  const { query, hits } = useBookSearch()

  const isEmptyQuery = query === ''
  const isEmptyHits = hits.length === 0

  const getResultsText = () => {
    if (isEmptyQuery) {
      return t({ id: 'search.emptySearchString', defaultMessage: 'Begin your search here.' })
    }
    if (!isEmptyQuery && isEmptyHits) {
      return t({
        id: 'search.noResultsFound',
        defaultMessage: 'No results found.'
      })
    }
    return t({
      id: 'header.search.noResultQuery',
      defaultMessage: 'No more results for this query'
    })
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      maxWidth={'xl'}
      gap={'20px'}
      sx={{ marginBottom: '60px', marginTop: '40px' }}
    >
      {<Typography variant={'h4'}>{getResultsText()}</Typography>}
      {showBackToTopButton && (
        <Button
          onClick={() => window.scrollTo(0, 0)}
          variant="outlined"
          color="primary"
          startIcon={<ArrowUpwardRounded />}
        >
          {t({ id: 'scroll.top', defaultMessage: 'Back to top' })}
        </Button>
      )}
    </Box>
  )
}

export const InfiniteHitsWrapper: React.FC = () => {
  const { hits, isLoading, nextPage, hasMore } = useBookSearch()
  const { activeRefinements } = useBookSearchRefinements()

  const includedFormats: string[] = []
  const includedLanguages: string[] = []

  activeRefinements.forEach((refinement) => {
    if (refinement.attribute === 'books.languages') {
      includedLanguages.push(refinement.value)
    } else if (refinement.attribute === 'books.format') {
      includedFormats.push(refinement.value)
    }
  })

  const isEmpty = hits.length === 0

  /* Verify this is an okay way of doing it. Done because both this and infinite list uses isLoading */
  if (isLoading && window.scrollY < 1000) {
    // Initial loading of results
    return <SkeletonLoadingList numberOfSkeletonCards={4} />
  }

  if (!isLoading && isEmpty) {
    // No results found
    return (
      <Box
        maxWidth="xl"
        width="100%"
        minHeight={'400px'}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        sx={{ flexGrow: 1, margin: 'auto', marginTop: '50px', marginBottom: '50px' }}
      >
        <Box
          sx={{
            borderRadius: '60px',
            backgroundColor: 'grey.100',
            width: '120px',
            height: '120px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <BaseImage
            src={'/img/search.png'}
            fallbackImageSrc={'/img/search.png'}
            alt="No results found"
            width={60}
            height={60}
          />
        </Box>
        <EmptyResultState showBackToTopButton={false} />
      </Box>
    )
  }
  return (
    <InfiniteList
      isLoading={isLoading}
      preloader={<SkeletonLoadingList numberOfSkeletonCards={20} />}
      loadMore={nextPage}
      hasMore={hasMore}
    >
      <List sx={{ minHeight: '40vh' }}>
        {hits.map((item) => (
          <SearchResultHit
            includedLanguages={includedLanguages}
            includedFormats={includedFormats}
            hit={item}
            key={`${item.id}`}
          />
        ))}
      </List>
      {!hasMore && hits.length > 10 && <EmptyResultState />}
    </InfiniteList>
  )
}
